import {datadogRum, RumEvent} from '@datadog/browser-rum';
import {getUsername, getProject} from '../_shared/GWTBridge/invoke/genPage';
import {
    getBaseUrl,
    getLanguage,
    isPluginDisabled,
    getAPIVersion,
    getPriorityVersion,
    getIsZoomInstallation,
    getPriorityServer
} from '../_shared/GWTBridge/invoke/common';
import {getCompanyName} from '../utils/proto-api-utils/utils';
import {getEnvType} from '../utils/platform';
import {getTabulaIni} from '../constants/metaData';
import {serverResponseToJSON} from '../_shared/GWTBridge/invoke/priResponse';
import {getCountry, getLangTitle, getProjType} from '../Localization';

const version = window['buildVersion'];
const env = getEnvType();

let isServiceCrossDomain: boolean;

const getMethodName = (responseText: string) => {
    let methodName;
    const a = responseText.split('Result>');
    if (a.length > 1) {
        const b = a[1].split('</');
        if (b.length > 1) {
            methodName = b[1];
        }
    }
    return methodName;
};

const getDatadogInfo = name => {
    const start = (window as any).priorityParams.indexOf(name);
    if (start === -1) return '';
    const end = (window as any).priorityParams.indexOf('&', start);
    return (window as any).priorityParams.substring(start + 1 + name.length, end);
};

export const initDataDogRum = () => {
    if (!isMonitor()) return;
    const clientToken = getDatadogInfo('_rumtoken');
    const clientId = getDatadogInfo('_rumid');
    const sampleRate = getDatadogInfo('_rumsamplerate');
    const sampleReplayRate = getDatadogInfo('_rumreplaysamplerate');
    const isCloud = window.location.host.includes('priority-connect');

    if (!clientToken || !clientId || !sampleRate || !sampleReplayRate || !isCloud) return;
    const urlParams = window.location.search;
    const priorityParams = (window as any).priorityParams;
    const isMonitorTest =
        urlParams.includes('_monitorTest') || priorityParams?.indexOf('_monitorTest') > -1;
    const sessionSampleRate = isMonitorTest ? 100 : Number(sampleRate);

    datadogRum.init({
        applicationId: clientId,
        clientToken: clientToken,
        site: 'datadoghq.eu',
        service: 'priority_ui',
        env: env,
        version: version,
        sessionSampleRate,
        sessionReplaySampleRate: Number(sampleReplayRate),
        trackInteractions: true,
        trackResources: true,
        defaultPrivacyLevel: 'mask',
        allowedTracingOrigins: getTracingOrigins(),
        beforeSend: (event: RumEvent, context) => {
            if (event?.type === 'error') {
                // log only errors caused by priority source
                if (event.error.source === 'source') {
                    // don't log handled application no data errors
                    if (event.error.message.includes('no data')) {
                        return false;
                    }

                    // don't log handled application Script error.
                    if (event.error.message.includes('Script error')) {
                        return false;
                    }
                    return true;
                }

                return false;
            } else if ((event?.resource as any)?.type === 'xhr') {
                if ('xhr' in context && context?.xhr?.responseType === '') {
                    let session: string;
                    const methodName = getMethodName(context?.xhr?.responseText);
                    const responseJSON = serverResponseToJSON(
                        context?.xhr?.responseText,
                        methodName
                    );
                    const addContext: {
                        step?: any;
                        finished?: any;
                        title?: any;
                        message?: any;
                        messagetype?: any;
                    } = {};
                    if (responseJSON?.Form?.length) {
                        if (responseJSON.Form[0].Position?.length) {
                            session = responseJSON.Form[0].Position[0].session;
                        }
                        if (responseJSON.Form[0].Error?.length) {
                            addContext.message = responseJSON.Form[0].Error[0].message;
                            addContext.messagetype = responseJSON.Form[0].Error[0].messagetype;
                        }
                        if (responseJSON.Form[0].Warning?.length) {
                            addContext.message = responseJSON.Form[0].Warning[0].message;
                            addContext.messagetype = responseJSON.Form[0].Warning[0].messagetype;
                        }
                    } else if (responseJSON?.Procedure?.length) {
                        session = responseJSON.Procedure[0].session;
                        addContext.step = responseJSON.Procedure[0].step;
                        addContext.finished = responseJSON.Procedure[0].finished;
                        addContext.title = responseJSON.Procedure[0].title;
                        if (responseJSON.Procedure[0].Message?.length) {
                            addContext.message = responseJSON.Procedure[0].Message[0].message;
                        }
                    }
                    const common = {
                        baseurl: getBaseUrl(),
                        language: getLanguage(),
                        isPluginInstalled: !isPluginDisabled() ? 'Yes' : 'No',
                        clientVersion: getAPIVersion(),
                        serverVersion: getPriorityVersion(),
                        isZoomInstallation: getIsZoomInstallation(),
                        username: getUsername(),
                        project: getProject(),
                        company: getCompanyName(),
                        uniqueUser: `${getUsername()}@${getProject()}`
                    };

                    event.context = {
                        ...event.context,
                        tenant: getTabulaIni().toLowerCase(),
                        isCrossDomain: isServiceCrossDomain ? 'Yes' : 'No',
                        ...common,
                        ...addContext
                    };

                    const responseData = {
                        session: session,
                        methodName: methodName
                    };

                    event.context = {
                        ...event.context,
                        ...responseData
                    };
                }
            }

            return true;
        }
    });

    if (isReplay()) {
        datadogRum.startSessionReplayRecording();
    }

    datadogRum.setUser({
        name: getUsername()
    });
};

const getTracingOrigins = (): string[] => {
    const url = (window as any).MyGetURL?.();
    const {origin: locationOrigin, host: locationHost} = window.location;

    if (url) {
        const domain = new URL(url);
        isServiceCrossDomain = domain.host !== locationHost;
        return [locationOrigin, domain.origin];
    }

    return [locationOrigin];
};

export const monitorAction = (name: string, values = {}) => {
    if (!isMonitor()) return;
    const monitorObjetc = {...getMonitorObject(), ...values};
    datadogRum.addAction(name, monitorObjetc);
};

export const isMonitor = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const dogDisabled =
        urlParams.get('_dog') === '0' || (window as any).priorityParams.indexOf('_dog=0') > -1;

    if (dogDisabled) {
        return false;
    }

    return true;
};

export const isReplay = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const replayEnabled =
        urlParams.get('_replay') === '1' ||
        (window as any).priorityParams.indexOf('_replay=1') > -1;

    const replayDisabled =
        urlParams.get('_replay') === '0' ||
        (window as any).priorityParams.indexOf('_replay=0') > -1;

    if (replayDisabled) {
        return false;
    }

    return replayEnabled;
};

type MonitorObject = {
    username: string;
    project: string;
    projectType: string;
    server: string;
    lang: string;
    country: string;
    isAws: boolean;
    isCloud: boolean;
    version: string;
    uniqueUser: string;
};

const getMonitorObject = (): MonitorObject => {
    const username = getUsername();
    const project = getProject();
    const projectType = getProjType();
    const server = getPriorityServer();
    const lang = getLangTitle();
    const country = getCountry();
    const isAws = window.location.href.indexOf('isaws=1') > -1;
    const isCloud = window.location.host.includes('priority-connect');
    const uniqueUser = `${username}@${project}`;
    return {
        username,
        project,
        projectType,
        server,
        lang,
        country,
        isAws,
        isCloud,
        version,
        uniqueUser
    };
};
